import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import { invalidateFor } from "@/lib/cache-registry";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { latestRunByProduct, PRODUCT_DATA_STATUS_ORDER, productDataStatus, type EnrichmentRun, type ProductDataStatus } from "@/lib/equipment/enrichment";
import type { DBEquipmentMedia, DBEquipmentProduct } from "@/lib/db-types";

const BUCKET = "equipment-media";
const PRODUCT_COLUMNS =
  "id, brand_id, brand_name, model, category_id, description, manufacturer_part_no, specs, product_url, enriched_at, created_at";

/** Modellen et utstyr er av (VIB-2918). */
export function useEquipmentProduct(productId: string | null | undefined) {
  return useQuery({
    queryKey: ["equipment-product", productId],
    enabled: !!productId,
    queryFn: async (): Promise<DBEquipmentProduct | null> => {
      const { data, error } = await supabase.from("equipment_products").select(PRODUCT_COLUMNS).eq("id", productId!).maybeSingle();
      if (error) throw error;
      return data as DBEquipmentProduct | null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export interface ProductMediaFile extends DBEquipmentMedia {
  url: string;
}

/** Produktbilder, manualer og datablad for en modell, med signerte lenker (privat bøtte). */
export function useProductMedia(productId: string | null | undefined) {
  return useQuery({
    queryKey: ["equipment-product-media", productId],
    enabled: !!productId,
    queryFn: async (): Promise<ProductMediaFile[]> => {
      const { data, error } = await supabase
        .from("equipment_media")
        .select("id, product_id, item_id, kind, storage_path, title, source_url, source, mime_type, size_bytes, created_at")
        .eq("product_id", productId!)
        .order("created_at");
      if (error) throw error;
      const rows = (data ?? []) as DBEquipmentMedia[];
      if (rows.length === 0) return [];
      const { data: signed, error: signError } = await supabase.storage
        .from(BUCKET)
        .createSignedUrls(rows.map((r) => r.storage_path), 3600);
      if (signError) throw signError;
      const urlByPath = new Map((signed ?? []).map((s) => [s.path, s.signedUrl]));
      return rows.flatMap((r) => (urlByPath.get(r.storage_path) ? [{ ...r, url: urlByPath.get(r.storage_path)! }] : []));
    },
    // Lenkene varer en time.
    staleTime: 30 * 60 * 1000,
  });
}

export interface ProductOverviewRow extends DBEquipmentProduct {
  itemCount: number;
  status: ProductDataStatus;
  latestRunId: string | null;
}

export interface ProductOverview {
  products: ProductOverviewRow[];
  skipped: { id: string; name: string; reason: string }[];
  withoutProduct: number;
}

/** /utstyr/produktdata: alle modeller med antall enheter og status, og rader som hoppes over. */
export function useProductOverview(enabled: boolean) {
  return useQuery({
    queryKey: ["equipment-products", "overview"],
    enabled,
    queryFn: async (): Promise<ProductOverview> => {
      const [products, items, runs] = await Promise.all([
        fetchAllPaginated<DBEquipmentProduct>("equipment_products", PRODUCT_COLUMNS),
        fetchAllPaginated<{ id: string; name: string; product_id: string | null; product_skip_reason: string | null }>(
          "equipment_items",
          "id, name, product_id, product_skip_reason",
          (q) => q.is("deleted_at", null),
        ),
        fetchAllPaginated<Pick<EnrichmentRun, "id" | "product_id" | "status" | "created_at">>(
          "equipment_enrichment_runs",
          "id, product_id, status, created_at",
        ),
      ]);
      const now = Date.now();
      const counts = new Map<string, number>();
      for (const i of items) if (i.product_id) counts.set(i.product_id, (counts.get(i.product_id) ?? 0) + 1);
      const latest = latestRunByProduct(runs);
      return {
        products: products
          .map((p) => ({
            ...p,
            itemCount: counts.get(p.id) ?? 0,
            status: productDataStatus(p.enriched_at, latest.get(p.id) ?? null, now),
            latestRunId: latest.get(p.id)?.id ?? null,
          }))
          .sort(
            (a, b) =>
              PRODUCT_DATA_STATUS_ORDER[a.status] - PRODUCT_DATA_STATUS_ORDER[b.status] ||
              `${a.brand_name} ${a.model}`.localeCompare(`${b.brand_name} ${b.model}`, "nb"),
          ),
        skipped: items
          .filter((i) => !i.product_id && i.product_skip_reason)
          .map((i) => ({ id: i.id, name: i.name, reason: i.product_skip_reason! }))
          .sort((a, b) => a.name.localeCompare(b.name, "nb")),
        withoutProduct: items.filter((i) => !i.product_id && !i.product_skip_reason).length,
      };
    },
  });
}

/**
 * Kobler et utstyr til en modell. Finnes merke + modell fra før (uten hensyn
 * til store og små bokstaver), gjenbrukes den; ellers opprettes den. En
 * produktlenke lagres på modellen (VIB-2936). Aktøren er den ekte brukeren
 * (RLS krever created_by = innlogget bruker).
 */
export function useLinkEquipmentProduct() {
  const qc = useQueryClient();
  const { selfUser } = useImpersonation();
  return useMutation({
    mutationFn: async ({ equipmentId, brand, model, productUrl = null }: { equipmentId: string; brand: string; model: string; productUrl?: string | null }): Promise<string> => {
      const brandName = brand.trim();
      const modelName = model.trim();
      if (!brandName || !modelName) throw new Error("Fyll ut merke og modell");
      const { data: existing, error: findErr } = await supabase
        .from("equipment_products")
        .select("id")
        .ilike("brand_name", brandName)
        .ilike("model", modelName)
        .maybeSingle();
      if (findErr) throw findErr;
      let productId = existing?.id as string | undefined;
      if (!productId) {
        const { data: knownBrand } = await supabase.from("equipment_brands").select("id, name").ilike("name", brandName).maybeSingle();
        const { data: created, error: insErr } = await supabase
          .from("equipment_products")
          .insert({ brand_id: knownBrand?.id ?? null, brand_name: knownBrand?.name ?? brandName, model: modelName, product_url: productUrl, created_by: selfUser?.id })
          .select("id")
          .single();
        if (insErr) throw new Error(`Kunne ikke opprette modellen: ${insErr.message}`);
        productId = created.id as string;
      } else if (productUrl) {
        await saveProductUrl(productId, productUrl);
      }
      const { error: linkErr } = await supabase
        .from("equipment_items")
        .update({ product_id: productId, product_skip_reason: null })
        .eq("id", equipmentId);
      if (linkErr) throw new Error(`Kunne ikke koble utstyret til modellen: ${linkErr.message}`);
      return productId;
    },
    onSuccess: async (productId, { equipmentId }) => {
      await invalidateFor(qc, "equipment_products", { productId });
      await invalidateFor(qc, "equipment_items", { equipmentId });
    },
  });
}

async function saveProductUrl(productId: string, productUrl: string | null) {
  const { data, error } = await supabase.from("equipment_products").update({ product_url: productUrl }).eq("id", productId).select("id");
  if (error) throw new Error(`Kunne ikke lagre produktlenken: ${error.message}`);
  if ((data ?? []).length !== 1) throw new Error("Du har ikke tilgang til å endre modellen");
}

/** Legger inn eller retter produktlenken på en modell (VIB-2936). */
export function useSetProductUrl() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ productId, productUrl }: { productId: string; productUrl: string | null }) => saveProductUrl(productId, productUrl),
    onSuccess: async (_res, { productId }) => {
      await invalidateFor(qc, "equipment_products", { productId });
    },
  });
}
